"use client";

import { Compass, Box, Leaf, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    index: "§ 02.1",
    icon: Compass,
    title: "Correct",
    italicWord: "",
    titleSuffix: "",
    description: "The step satisfies the relevant mark-scheme criterion outright: full credit, no ambiguity.",
    to: "/ClassificationTaxonomyPage#correct"
  }, {
    index: "§ 02.2",
    icon: Box,
    title: "Process Error",
    italicWord: "Error",
    titleSuffix: "",
    description: "The method is right, but execution slipped: an arithmetic mistake, a sign error, a mis-substitution. Method is credited even where a mark is lost.",
    to: "/ClassificationTaxonomyPage#process-error"
  }, {
    index: "§ 02.3",
    icon: Leaf,
    title: "Knowledge Gap",
    italicWord: "Gap",
    titleSuffix: "",
    description: "The method itself is wrong: the step shows the student doesn't understand what this part of the question requires.",
    to: "/ClassificationTaxonomyPage#knowledge-gap"
  }];
  return <section data-section-id="2110" id="data-formats" className="relative overflow-hidden bg-background text-foreground py-28 md:py-36">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute right-[-4%] top-[2%] hidden select-none md:block" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "clamp(320px, 52vw, 760px)",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(254 20% 85%)",
      opacity: 0.1,
      fontWeight: 350
    }}>
        02
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-[-8%] top-[1%] block select-none md:hidden" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "320px",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(254 20% 85%)",
      opacity: 0.1,
      fontWeight: 350
    }}>
        02
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            {}
            <div className="flex items-center gap-2 uppercase" style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.6875rem",
            fontWeight: 500,
            letterSpacing: "0.18em",
            lineHeight: 1
          }}>
              <span className="text-primary">§ 02</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">CLASSIFICATION TAXONOMY</span>
            </div>

            {}
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-lg bg-primary" />
                <span className="h-1 w-1 rounded-lg bg-primary" />
                <span className="h-1 w-1 rounded-lg bg-primary" />
              </div>
            </div>

            {}
            <h2 className="mt-8 font-default text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 350,
            letterSpacing: "-0.02em",
            lineHeight: 1.02
          }}>
              Three outcomes,{" "}
              <br className="hidden sm:block" />
              one{" "}
              <em className="font-default" style={{
              fontStyle: "italic",
              fontWeight: 350
            }}>
                taxonomy
              </em>
              .
            </h2>
          </div>

          <div className="lg:col-span-5 lg:flex lg:items-end">
            <p className="font-default max-w-md text-muted-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.55
          }}>
              Every step of a student's working is classified into exactly one of three outcomes: a shared vocabulary that ties raw working back to mark-scheme logic.
            </p>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="mt-20 grid grid-cols-1 gap-6 md:gap-7 lg:grid-cols-12 lg:gap-6">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={i} data-index={i} className={`lg:col-span-4 transition-all ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDuration: "720ms",
            transitionDelay: `${i * 80}ms`
          }}>
                <Card className="group relative h-full overflow-hidden p-9 transition-transform transition-colors duration-300 ease-out hover:-translate-y-1 hover:border-primary bg-card shadow-md rounded-lg border-border">
                  {}
                  <span aria-hidden="true" className="pointer-events-none absolute left-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 border-t border-l border-primary" />
                  <span aria-hidden="true" className="pointer-events-none absolute right-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 border-t border-r border-primary" />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 left-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 border-b border-l border-primary" />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 border-b border-r border-primary" />

                  {}
                  <div className="flex items-center gap-2 uppercase" style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.6875rem",
                fontWeight: 500,
                letterSpacing: "0.18em",
                lineHeight: 1
              }}>
                    <span className="text-primary">{pillar.index}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">PRACTICE</span>
                  </div>

                  {}
                  <div className="mt-8 transition-transform duration-[400ms] ease-out group-hover:rotate-6 text-primary">
                    <Icon size={32} strokeWidth={1.25} />
                  </div>

                  {}
                  <h3 className="mt-7 font-default text-foreground" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
                fontWeight: 400,
                letterSpacing: "-0.012em",
                lineHeight: 1.15
              }}>
                    {pillar.italicWord ? pillar.title.replace(new RegExp(`\\s*${pillar.italicWord}$`), "") : pillar.title}
                    {pillar.italicWord && <>
                        {" "}
                        <em className="font-default" style={{
                    fontStyle: "italic",
                    fontWeight: 400
                  }}>
                          {pillar.italicWord}
                        </em>
                      </>}
                  </h3>

                  {}
                  <p className="font-default mt-5 text-muted-foreground" style={{
                fontSize: "1rem",
                fontWeight: 400,
                letterSpacing: "0.005em",
                lineHeight: 1.65
              }}>
                    {pillar.description}
                  </p>

                  {}
                  <div className="mt-9 flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <div className="flex items-center gap-1">
                      <span className="h-1 w-1 rounded-lg bg-primary" />
                      <span className="h-1 w-1 rounded-lg bg-primary" />
                      <span className="h-1 w-1 rounded-lg bg-primary" />
                    </div>
                  </div>

                  {}
                  <div className="mt-6">
                    <Link to={pillar.to} className="group/link relative inline-flex items-center gap-2 font-default text-primary" style={{
                  fontSize: "0.9375rem",
                  fontWeight: 450
                }}>
                      <span className="relative">
                        Study protocol
                        <span className="absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-300 ease-out group-hover/link:w-full bg-primary" />
                      </span>
                      <ArrowRight size={16} strokeWidth={1.25} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                    </Link>
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}